import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Persistent per-browser device id — lets the backend spot when one account is used on many devices.
const DEVICE_KEY = "aiof_device_id";
export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch { return ""; }
}

api.interceptors.request.use((config) => {
  const id = getDeviceId();
  if (id) config.headers["X-Device-Id"] = id;
  return config;
});

// Single-flight 401 -> refresh -> retry. Keeps long sessions (exams) alive.
let refreshPromise = null;
const AUTH_SKIP = ["/auth/refresh", "/auth/login", "/auth/logout", "/auth/register", "/auth/me"];
function isAuthExcluded(config) {
  const url = config?.url || "";
  return config?._skipAuthRefresh || AUTH_SKIP.some((p) => url.includes(p));
}
function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api
      .post("/auth/refresh", null, { _skipAuthRefresh: true })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status !== 401 || !original || original._retry || isAuthExcluded(original)) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      await refreshSession();
      return api(original);
    } catch (e) {
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
      return Promise.reject(e);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Stream an agent chat message via SSE (fetch reader).
export async function streamAgentMessage(sessionId, content, { onDelta, onDone, onError }) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/sessions/${sessionId}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      let msg = `Erreur ${res.status}`;
      try { const j = await res.json(); msg = formatApiErrorDetail(j.detail); } catch {}
      onError?.(msg, res.status);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        const line = part.trim();
        if (line.startsWith("data:")) {
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.delta) onDelta?.(data.delta);
            if (data.error) onError?.(data.error);
          } catch {}
        }
      }
    }
    onDone?.();
  } catch (e) {
    onError?.(e.message);
  }
}

export default api;
